import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Match {
  id: string;
  leagueId: string;
  homeTeamId: string;
  awayTeamId: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  status: string;
  homeScore?: number;
  awayScore?: number;
  homeTeam: { id: string; name: string } | null;
  awayTeam: { id: string; name: string } | null;
  league: { id: string; name: string } | null;
}

function formatWeekdayDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
}

const MatchDay: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.profileType !== 'referee') {
      navigate('/dashboard');
      return;
    }
    fetchMatches();
  }, [user, navigate]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const response = await api.get('/referees/matches');
      const fetchedMatches: Match[] = response.data.matches || response.data || [];
      setMatches(fetchedMatches);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const sevenDaysFromNow = new Date(now);
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  const futureStr = sevenDaysFromNow.toISOString().split('T')[0];

  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const pastStr = sevenDaysAgo.toISOString().split('T')[0];

  const upcoming = matches
    .filter(m => m.status === 'scheduled' && m.scheduledDate >= todayStr && m.scheduledDate <= futureStr)
    .sort((a, b) => {
      const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime || '00:00'}`);
      const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime || '00:00'}`);
      return dateA.getTime() - dateB.getTime();
    });

  const recentlyCompleted = matches
    .filter(m => m.status === 'completed' && m.scheduledDate >= pastStr && m.scheduledDate <= todayStr)
    .sort((a, b) => {
      const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime || '00:00'}`);
      const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime || '00:00'}`);
      return dateB.getTime() - dateA.getTime();
    });

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <h1 style={{ color: '#FFC107' }}>Match Day</h1>
      <p style={{ color: '#757575', marginBottom: '30px' }}>
        Your assignments at a glance
      </p>

      <h2 style={{ marginBottom: '15px' }}>Upcoming Assignments</h2>
      {upcoming.length === 0 ? (
        <div className="card" style={{ marginBottom: '30px' }}>
          <p>No upcoming matches in the next 7 days.</p>
        </div>
      ) : (
        <div className="grid" style={{ marginBottom: '30px' }}>
          {upcoming.map((match) => (
            <div key={match.id} className="card">
              <h3 style={{ marginBottom: '5px' }}>
                {match.homeTeam?.name || 'TBD'} vs {match.awayTeam?.name || 'TBD'}
              </h3>
              {match.league && (
                <p style={{ color: '#757575', fontSize: '14px', marginBottom: '10px' }}>{match.league.name}</p>
              )}
              <div style={{ marginBottom: '15px' }}>
                <p><strong>Date:</strong> {formatWeekdayDate(match.scheduledDate)}</p>
                {match.scheduledTime && <p><strong>Time:</strong> {formatTime(match.scheduledTime)}</p>}
                {match.location && <p><strong>Location:</strong> {match.location}</p>}
              </div>
              <Link
                to={`/referees/matches/${match.id}/complete`}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                Complete Match
              </Link>
            </div>
          ))}
        </div>
      )}

      <h2 style={{ marginBottom: '15px' }}>Recently Completed</h2>
      {recentlyCompleted.length === 0 ? (
        <div className="card">
          <p>No completed matches in the last 7 days.</p>
        </div>
      ) : (
        <div className="grid">
          {recentlyCompleted.map((match) => (
            <div key={match.id} className="card">
              <h3 style={{ marginBottom: '5px' }}>
                {match.homeTeam?.name || 'TBD'} vs {match.awayTeam?.name || 'TBD'}
              </h3>
              {match.homeScore !== undefined && match.awayScore !== undefined && (
                <p style={{ fontSize: '20px', fontWeight: 'bold', margin: '5px 0 10px' }}>
                  {match.homeScore} - {match.awayScore}
                </p>
              )}
              {match.league && (
                <p style={{ color: '#757575', fontSize: '14px', marginBottom: '10px' }}>{match.league.name}</p>
              )}
              <div style={{ marginBottom: '15px' }}>
                <p><strong>Date:</strong> {formatWeekdayDate(match.scheduledDate)}</p>
                {match.location && <p><strong>Location:</strong> {match.location}</p>}
              </div>
              <Link
                to={`/referees/matches/${match.id}`}
                className="btn btn-secondary"
                style={{ width: '100%' }}
              >
                View Details
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchDay;
